/**
 * Device detection — single source of truth for mobile/tablet/desktop.
 * Breakpoints: Mobile ≤767px, Tablet 768–1199px, Desktop ≥1200px.
 */

import { useState, useEffect } from "react";

export const isMobile = (): boolean =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTablet = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia("(min-width: 768px) and (max-width: 1199px)").matches;

export const isDesktop = (): boolean =>
  typeof window !== "undefined" && window.matchMedia("(min-width: 1200px)").matches;

export type ViewportSize = "mobile" | "tablet" | "desktop";

export function getViewportSize(): ViewportSize {
  if (typeof window === "undefined") return "desktop";
  if (isMobile()) return "mobile";
  if (isTablet()) return "tablet";
  return "desktop";
}

/** React hook: re-renders when viewport crosses breakpoints. */
export function useViewport(): ViewportSize {
  const [size, setSize] = useState<ViewportSize>(getViewportSize);

  useEffect(() => {
    const mqMobile = window.matchMedia("(max-width: 767px)");
    const mqTablet = window.matchMedia("(min-width: 768px) and (max-width: 1199px)");

    const update = () => setSize(getViewportSize());
    mqMobile.addEventListener("change", update);
    mqTablet.addEventListener("change", update);
    window.addEventListener("resize", update);
    return () => {
      mqMobile.removeEventListener("change", update);
      mqTablet.removeEventListener("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return size;
}
